import { SeverityLevel } from '@microsoft/applicationinsights-web';
import {
    getCachedLanguage,
    getEnvironment,
} from '@uipath/portal-shell-util';
import { SeverityLevel as TelemetryClientSeverity } from '@uipath/telemetry-client-web';

import { IAppInsightsModel } from '../../models/appInsights/appInsightsModel';
import { ErrorOrCriticalPayload } from '../../models/appInsights/eventMap';

export default class ConsumerLogger {
    static appInsights: IAppInsightsModel;
    static isInitialized = false;

    /**
     * Sets the app insights instance to ConsumerLogger
     * @param appInsights App insights instance to set
     */
    public static setAppInsights(appInsights: IAppInsightsModel) {
        this.appInsights = appInsights;
        this.appInsights.initializeTracker(
            [ 'local', 'local-msi' ].includes(getEnvironment()),
            getCachedLanguage() ?? 'en',
        );

        this.isInitialized = true;
    }

    /**
     * For business telemetry only, logs custom events to AppInsights.
     * Respects the new standardized format for logging
     *
     * @param event event name to track
     * @param details details to track in custom dimension
     * @param severityLevel severity level to track in custom dimensions (defaults to Information)
     */
    public static logCustomEvent(
        event: string,
        details?: Record<string, any>,
        severityLevel: SeverityLevel = SeverityLevel.Verbose,
    ) {
        this.appInsights?.trackerMethods?.trackEvent(event as any, details);

        if (severityLevel > SeverityLevel.Warning) {
            const message = this.getFormattedMessage(event, (
                details as unknown as ErrorOrCriticalPayload
            )?.errorMessage ?? 'Unknown error');

            this.logError(
                new Error(message),
                severityLevel === SeverityLevel.Error ? TelemetryClientSeverity.Major : TelemetryClientSeverity.Critical,
                details ?? {},
            );
        }
    }

    /**
     * Logs a trace to AppInsights
     *
     * @param message
     * @param severityLevel
     * @param details
     */
    public static logTrace(message: string, severityLevel: TelemetryClientSeverity, details: Record<string, any>) {
        this.appInsights?.trackerMethods?.trackTrace(message, severityLevel, details);
    }

    /**
     * Logs an error to AppInsights
     *
     * @param error
     * @param severityLevel
     * @param details
     */
    public static logError(error: Error, severityLevel: TelemetryClientSeverity, details: Record<string, any>) {
        this.appInsights?.trackerMethods?.trackError(error, severityLevel, details);
    }

    private static getFormattedMessage(scope: string | string[], message: string) {
        return `[${scope}] - ${message}`;
    }
}
